import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Autocomplete, Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { departureSelectorKey, groupDeparturesForSelector } from './departureRows';

type SelectorDeparture = { departureName: string | null; departureDate?: string };

type DepartureView = 'upcoming' | 'departed';

interface Props<T extends SelectorDeparture> {
  departures: Array<T>;
  selectedDepartureKey: string;
  onSelectDeparture: (departureKey: string) => void;
  /** Flags a departure in the list, e.g. one with no price yet. */
  renderTag?: (group: Array<T>) => React.ReactNode;
}

const NOT_ON_TOUR = 'Not on this tour';

function formatDate(date?: string): string {
  return date ? dayjs(date).format('D MMM YYYY') : NOT_ON_TOUR;
}

function monthOf<T extends SelectorDeparture>(group: Array<T>): string {
  const date = group[0].departureDate;
  return date ? dayjs(date).format('MMMM YYYY') : NOT_ON_TOUR;
}

/** A selection shows in its own list; otherwise the list last chosen, then whichever has departures. */
function currentView(
  selectedView: DepartureView | null,
  chosenView: DepartureView | null,
  startOnUpcoming: boolean,
): DepartureView {
  return selectedView ?? chosenView ?? (startOnUpcoming ? 'upcoming' : 'departed');
}

/**
 * Picks one departure, or none for all of them. Upcoming departures read soonest
 * first and departed ones most recent first, each grouped by month and
 * searchable by name or date.
 */
export default function TourDepartureSelector<T extends SelectorDeparture>({
  departures,
  selectedDepartureKey,
  onSelectDeparture,
  renderTag,
}: Props<T>) {
  const today = dayjs().format('YYYY-MM-DD');

  const { upcoming, departed } = useMemo(() => {
    const groups = groupDeparturesForSelector(departures)
      .map(([, group]) => group)
      // Dated chronologically; one the tour no longer runs has no date and goes last.
      .sort(([a], [b]) =>
        a.departureDate && b.departureDate ? a.departureDate.localeCompare(b.departureDate) : a.departureDate ? -1 : 1,
      );
    const isDeparted = (group: Array<T>) => !!group[0].departureDate && group[0].departureDate < today;

    return {
      upcoming: groups.filter((group) => !isDeparted(group)),
      departed: groups.filter(isDeparted).reverse(),
    };
  }, [departures, today]);

  const selected = useMemo(
    () => upcoming.concat(departed).find((group) => departureSelectorKey(group[0]) === selectedDepartureKey) ?? null,
    [departed, selectedDepartureKey, upcoming],
  );

  const [chosenView, setChosenView] = useState<DepartureView | null>(null);
  const selectedView: DepartureView | null = selected ? (departed.includes(selected) ? 'departed' : 'upcoming') : null;
  const view = currentView(selectedView, chosenView, !!upcoming.length || !departed.length);
  const options = view === 'upcoming' ? upcoming : departed;

  const handleViewChange = (next: DepartureView | null) => {
    if (!next) {
      return;
    }

    // The selection sits in the other list, so switching clears it.
    setChosenView(next);
    onSelectDeparture('');
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
      <ToggleButtonGroup exclusive size="small" value={view} onChange={(_event, next) => handleViewChange(next)}>
        <ToggleButton value="upcoming" sx={{ px: 1.5 }}>
          Upcoming ({upcoming.length})
        </ToggleButton>
        <ToggleButton value="departed" sx={{ px: 1.5 }}>
          Departed ({departed.length})
        </ToggleButton>
      </ToggleButtonGroup>

      <Autocomplete
        options={options}
        value={selected}
        onChange={(_event, group) => onSelectDeparture(group ? departureSelectorKey(group[0]) : '')}
        groupBy={monthOf}
        getOptionLabel={([departure]) => `${departure.departureName ?? ''} · ${formatDate(departure.departureDate)}`}
        isOptionEqualToValue={([option], [value]) => departureSelectorKey(option) === departureSelectorKey(value)}
        renderOption={(props, group) => {
          const [departure] = group;

          return (
            <Box component="li" {...props} key={departureSelectorKey(departure)}>
              <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ minWidth: 116 }}>
                  {departure.departureName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(departure.departureDate)}
                </Typography>
                <Box flex={1} />
                {renderTag?.(group)}
              </Stack>
            </Box>
          );
        }}
        noOptionsText={view === 'upcoming' ? 'No upcoming departures' : 'No departures have left yet'}
        ListboxProps={{ style: { maxHeight: 420 } }}
        sx={{ minWidth: 420, maxWidth: 560, flex: 1 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Departure"
            placeholder="All departures · search by name or date"
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
    </Stack>
  );
}
